// src/hooks/useReport.js

import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useOrganization } from '../shared/OrganizationContext';
import apiClient from '../ctapi/apiClient';

// This function transforms the nested API response into the flat structure the UI components expect.
const transformReportData = (apiData) => {
    if (!apiData || !apiData.report) {
        console.error("[transformReportData] Invalid or missing apiData.report object", apiData);
        return null;
    }

    console.log("[transformReportData] Starting transformation with raw report:", apiData.report);

    const report = apiData.report;
    const overview = report.overview || {};
    const statusBreakdown = overview.status_breakdown || {};
    const qualityMetrics = report.candidate_quality_metrics || {};
    const genderDist = report.candidate_demographics?.gender_distribution || {};
    const engagement = report.engagement_insights || {};

    const transformed = {
        job_title: report.job_title || 'N/A',
        department: report.department || 'N/A',
        report_start_date: report.report_start_date || new Date().toISOString(),
        report_end_date: report.report_end_date || new Date().toISOString(),
        total_applicants: overview.total_applicants || 0,
        avg_applications_per_day: overview.avg_applications_per_day || 0,
        
        // Using available status breakdown data
        screened_count: statusBreakdown.screened || 0,
        approved_count: statusBreakdown.approved || 0,
        declined_count: statusBreakdown.declined || 0,
        pending_count: statusBreakdown.pending || 0,

        // These fields are not in the current API response, so we default them to 0.
        interviewed_count: statusBreakdown.interviewed || 0, 
        offer_count: statusBreakdown.offer || 0,
        hired_count: statusBreakdown.hired || 0,

        male_percent: genderDist.male_percent || 0,
        female_percent: genderDist.female_percent || 0,
        other_percent: genderDist.other_percent || 0,
        undisclosed_percent: genderDist.undisclosed_percent || 0,
        
        top_locations: report.top_locations || [],
        sources: report.sources || [],
        
        avg_qualification_score: qualityMetrics.avg_qualification_score || 0,
        avg_skills_match_percent: qualityMetrics.avg_skills_match_percent || 0,
        avg_experience: qualityMetrics.avg_experience_years || 0,
        
        top_hours: (engagement.most_active_hours || []).join(' & ') || 'N/A',
        top_day: engagement.peak_day_of_week || 'N/A',
        avg_time_to_apply: engagement.avg_time_to_apply_hours || 0,
        screening_pass_rate: report.funnel_performance?.screening_pass_rate_percent || 0,
    };

    console.log("[transformReportData] Transformation complete. Result:", transformed);
    return transformed;
};


export const useReport = (candidateId) => {
    const { isLoading: authLoading } = useAuth();
    const { organization } = useOrganization();
    
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchReport = async () => {
            if (authLoading) return;

            if (!candidateId || !organization) {
                setError('Organization or candidate ID not found');
                setLoading(false);
                return;
            }
            
            setLoading(true);
            setError(null);
            
            try {
                const endpoint = `organizations/${organization.name}/ideal-candidates/${candidateId}/matches?report=true`;
                const data = await apiClient.get(endpoint);

                if (data && data.report) {
                    const transformedData = transformReportData(data);
                    setReportData(transformedData);
                } else {
                    throw new Error("Report data is missing from the API response.");
                }

            } catch (err) {
                console.error('[useReport] API Error:', err);
                setError(`Failed to load report: ${err.message}.`);
                setReportData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchReport();
        
    }, [authLoading, candidateId, organization]);

    return { reportData, loading, error };
};